import React from 'react'
import styled, { css } from 'styled-components'
import { string, func, bool } from 'prop-types'
import { rgba, stripUnit } from 'polished'

import * as spacing from 'styles/spacing'
import { fontWeights, fontSizes } from 'styles/typography'
import { borderRadius, animationTime, animationCurve, square } from 'styles/global'
import { white, red, greyDark, black, greyLight } from 'styles/colors'
import { media, mediaDown } from 'styles/media'
import Fitty from 'components/Fitty'

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${white};
  cursor: pointer;
  box-shadow: 0px 0px 68px ${rgba(26, 32, 35, 0.07)};
  border-radius: ${borderRadius};
  width: 100%;
  text-align: center;
  padding: ${stripUnit(spacing.small) * 1.5 + 'px'} ${spacing.medium};
  border: 2px solid transparent;
  transition: border-color ${animationTime} ${animationCurve};

  ${mediaDown.medium`
    box-shadow: 0px 2px 8px ${rgba(26, 32, 35, 0.1)};
  `};

  ${props =>
    props.isLarge &&
    css`
      ${media.medium`
        padding: ${spacing.medium};
      `};

      ${media.large`
        padding: ${spacing.large};
      `};
    `}

  ${props =>
    props.selected &&
    css`
      border-color: inherit;
    `}
`

const Name = styled.span`
  font-weight: ${fontWeights.medium};
  color: ${greyLight};
  font-size: ${fontSizes.small};

  ${props =>
    props.isLarge &&
    css`
      font-weight: ${fontWeights.bold};
      color: ${greyDark};
      font-size: inherit;
    `}

  ${props =>
    props.pattern &&
    css`
      ${media.medium`
        padding-bottom: ${spacing.small};
      `};
    `}
`

const FigureWrap = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${spacing.small};

  ${props =>
    props.isLarge &&
    css`
      height: 150px;
      display: flex;
      align-items: center;
      justify-content: center;

      ${media.medium`
        margin-bottom: ${spacing.medium};
      `};

      ${media.xLarge`
        margin-bottom: ${spacing.large};
      `};
    `}
`

const FontPreview = styled.div`
  width: 100%;
  max-width: 80px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  color: ${black};

  ${media.medium`
    margin-top: ${spacing.tiny};
  `};
`

const PatternWrap = styled.div`
  position: relative;
  width: 40px;
  margin-left: auto;
  margin-right: auto;
  overflow: hidden;

  &:after {
    content: '';
    width: 100%;
    display: block;
    padding-bottom: 420%;
  }
`

const Pattern = styled.img`
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`

const Icon = styled.img`
  object-fit: cover;
  ${square('56px')};

  ${media.medium`
    ${square('56px')};
  `};
`

const Figure = styled.img`
  width: 100%;
  max-width: 82px;
  height: auto;

  ${media.small`
    max-width: 100px;
  `};

  ${media.medium`
    max-width: 115px;
  `};
`

const NewTag = styled.span`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  display: inline-block;
  background-color: ${red};
  color: ${white};
  text-transform: uppercase;
  font-weight: ${fontWeights.medium};
  font-size: 10px;
  padding: 2px ${spacing.small};
  border-radius: ${borderRadius};
  white-space: nowrap;
`

const EngravingOption = ({
  name,
  figure,
  fontFamily,
  text,
  pattern,
  icon,
  onClick,
  isLarge,
  selected,
  isNew,
  isLimitedEdition
}) => (
  <Container onClick={onClick} selected={selected} isLarge={isLarge}>
    {isNew && <NewTag>New</NewTag>}
    {isLimitedEdition && <NewTag>Limited Edition</NewTag>}
    <FigureWrap isLarge={isLarge}>
      {figure && (
        <Figure src={`${process.env.BASE_PATH}/images/engraving/options/${figure}.png`} alt={`${name} option`} />
      )}
      {icon && <Icon src={icon} alt={`${name} icon`} />}
      {text && (
        <FontPreview
          css={`
            font-family: ${fontFamily};
          `}
        >
          <Fitty>{text}</Fitty>
        </FontPreview>
      )}
      {pattern && (
        <PatternWrap>
          <Pattern src={pattern} alt={`${name} pattern`} />
        </PatternWrap>
      )}
    </FigureWrap>
    <Name pattern={pattern} isLarge={isLarge}>
      {name}
    </Name>
  </Container>
)

EngravingOption.propTypes = {
  name: string.isRequired,
  figure: string,
  icon: string,
  fontFamily: string,
  pattern: string,
  text: string,
  onClick: func,
  selected: bool,
  isNew: bool,
  isLimitedEdition: bool,
  isLarge: bool
}

export default EngravingOption
